import { SportWatcher } from '../components/SportWatcher';
import { RowArrows } from '../components/RowArrows';
import { PlexSlowNotice } from '../components/PlexSlowNotice';
import { useEffect, useMemo, useState } from 'react';
import { api, ApiError, artApi, call, type Me } from '../api';
import { setSpatialLabels } from '../meta';
import { Ctx, type AppContext } from '../context';
import { PlexClient, type PlexLibrary } from '../plex';
import { useRoute } from '../router';
import { TopNav } from '../components/TopNav';
import { Home } from './Home';
import { Library } from './Library';
import { Detail } from './Detail';
import { Search } from './Search';
import { Player } from './Player';
import { Live } from './Live';
import { LivePlayer } from './LivePlayer';
import { Multiview } from './Multiview';
import { ReminderToasts } from '../components/ReminderToasts';
import { isSettingsSection, Settings } from './Settings';
import { resetOwner } from '../owner';
import { setLabelPrefs } from '../labels';
import { Requests } from './Requests';
import { Editor } from './Editor';
import { Calendar } from './Calendar';
import { PersonPage } from './Person';
import { ErrorBoundary } from '../components/ErrorBoundary';
import { BackdropLayer } from '../components/BackdropLayer';
import { loadProfileTheme } from '../theme';
import { SkinStudio } from './SkinStudio';
import { RandomPick } from './RandomPick';
import { Swipe } from './Swipe';
import { ExtTitlePage } from '../components/Widgets';
import { FriendRecs } from '../components/Recommend';
import { Profile } from './Profile';
import { TrophyWatcher } from '../components/Trophies';
import { Stats } from './StatsPage';
import { CinemaGate } from '../components/Cinema';
import { Wrapped, WrappedBanner } from './Wrapped';
import { StatusPage } from './Status';
import { Browse } from './Browse';
import { AddonTitle } from './AddonTitle';
import { AddonPlayer } from './AddonPlayer';
import { Music } from './Music';
import { ServerPage } from './Server';
import { SportCenterPage } from './SportCenter';
import { MiniPlayer } from '../components/MiniPlayer';
import { FullPlayer } from '../components/FullPlayer';
import { TogetherJoin } from './TogetherJoin';
import { SportLeague } from './SportLeague';
import { MyList } from './MyList';
import { Collections } from './Collections';
import { SagaPage } from './Saga';
import { ListView } from './ListView';
import { RemotePage } from './Remote';
import { RemoteScreen } from '../components/RemoteScreen';
import { CardMenuHost } from '../components/CardMenu';
import { SeasonalLayer } from '../components/SeasonalLayer';
import { RequestWatcher } from '../components/RequestWatcher';
import { Screensaver } from '../components/Screensaver';
import { BedtimeGate } from '../components/BedtimeGate';
import { startLibraryEvents } from '../libraryEvents';
import { musicPlaying, setMusicPlex, stopMusic } from '../musicPlayer';
import { refreshAddons } from '../addons';
import { usePrefs } from '../prefs';

/** Alles na het kiezen van een profiel: Plex-verbinding, navigatie en de schermen. */
export function Main({ me, onSwitchProfile }: { me: Me; onSwitchProfile: () => void }) {
  const route = useRoute();
  const [plex, setPlex] = useState<PlexClient | null>(null);
  const [libraries, setLibraries] = useState<PlexLibrary[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Live bibliotheek: nieuwe titels uit Plex verschijnen zonder te verversen
  useEffect(() => startLibraryEvents(), []);

  // Netflix-look: OLED-zwart per profiel
  const prefs = usePrefs();
  // Labels op posters: eigen keuze van het profiel boven die van de skin
  useEffect(() => setLabelPrefs(prefs), [prefs.posterLabels, prefs.posterLabelsPos, prefs.posterLabelSize, prefs.posterScore, prefs.posterScorePos, prefs.posterLabelStack]);
  useEffect(() => {
    document.documentElement.dataset.oled = prefs.oled ? 'on' : 'off';
  }, [prefs.oled]);

  // Elk profiel heeft een eigen skin (geldt op elk apparaat)
  useEffect(() => {
    void loadProfileTheme();
    // Addons en muziek zijn per profiel
    refreshAddons();
    resetOwner();
    return () => stopMusic();
  }, [me.profile?.uuid]);

  useEffect(() => {
    api.plexConnection()
      .then(async (c) => {
        const client = new PlexClient(c.url, c.token);
        // Leeftijdsgrens van dit profiel: geldt voor alles wat uit Plex komt
        artApi.config().then((a) => setSpatialLabels(a.spatialLabels)).catch(() => undefined);
        call<{ policy: { maxAge?: number | null } }>('/api/policy').then((r) => client.setMaxAge(r.policy.maxAge ?? null)).catch(() => client.setMaxAge(null));
        setPlex(client);
        setMusicPlex(client);
        setLibraries(await client.libraries());
      })
      .catch((err) => {
        // Account of profiel zonder Plex: tumedia werkt dan met addons, IPTV en muziek
        if (err instanceof ApiError && err.code === 'no_plex') {
          const client = PlexClient.offline();
          artApi.config().then((a) => setSpatialLabels(a.spatialLabels)).catch(() => undefined);
          setPlex(client);
          setMusicPlex(client);
          setLibraries([]);
          return;
        }
        setError('Geen verbinding met de Plex-server. Is plex.tumedia.nl bereikbaar vanaf dit apparaat?');
      });
  }, []);

  const ctx = useMemo<AppContext | null>(
    () => (plex ? { me, plex, libraries, switchProfile: onSwitchProfile } : null),
    [me, plex, libraries, onSwitchProfile],
  );

  if (error) return <main className="center-screen"><p className="error lead" role="alert">{error}</p></main>;
  if (!ctx) return <main className="center-screen" aria-busy="true"><div className="logo">tu<span>media</span></div></main>;

  return (
    <Ctx.Provider value={ctx}>
      <ErrorBoundary key={location.pathname}>
      {route.name === 'play' ? (
        <CinemaGate key={`c${route.ratingKey}`} ratingKey={route.ratingKey} params={route.params}>
          <Player key={route.ratingKey + route.params.toString()} ratingKey={route.ratingKey} params={route.params} />
        </CinemaGate>
      ) : route.name === 'livePlay' ? (
        <LivePlayer key={`${route.accountId}:${route.streamId}:${route.start ?? ''}`} accountId={route.accountId} streamId={route.streamId} start={route.start} end={route.end} />
      ) : route.name === 'addonPlay' ? (
        <AddonPlayer key={route.params.get('key') ?? ''} params={route.params} />
      ) : route.name === 'multiview' ? (
        <Multiview />
      ) : (
        <>
          {(route.name === 'home' || route.name === 'hub' || route.name === 'library') && <BackdropLayer />}
          <TopNav route={route} />
          {route.name === 'home' && <WrappedBanner />}
          {route.name === 'home' && <FriendRecs />}
          {route.name === 'home' && <Home />}
          {route.name === 'hub' && <Home key={route.id} hubId={route.id} />}
          {route.name === 'editor' && <Editor />}
          {route.name === 'library' && <Library key={route.key} sectionKey={route.key} />}
          {route.name === 'item' && <Detail key={route.ratingKey} ratingKey={route.ratingKey} />}
          {route.name === 'search' && <Search initial={route.query} />}
          {route.name === 'live' && <Live tab={route.tab} />}
          {route.name === 'settings' && <Settings key={route.section ?? 'av'} section={isSettingsSection(route.section) ? route.section : undefined} />}
          {route.name === 'requests' && <Requests initial={route.query} />}
          {route.name === 'calendar' && <Calendar />}
          {route.name === 'mylist' && <MyList />}
          {route.name === 'collections' && <Collections />}
          {route.name === 'listView' && <ListView key={route.src} src={route.src} />}
          {route.name === 'saga' && <SagaPage key={route.id} id={route.id} />}
          {route.name === 'sportLeague' && <SportLeague key={route.id} id={route.id} />}
          {route.name === 'random' && <RandomPick />}
          {route.name === 'extTitle' && <ExtTitlePage key={`${route.type}:${route.tmdbId}:${route.params.toString()}`} type={route.type} tmdbId={route.tmdbId} params={route.params} />}
          {route.name === 'swipe' && <Swipe key={route.code ?? 'new'} code={route.code} />}
          {route.name === 'profile' && <Profile key={`${route.tab}:${route.friend ?? ''}`} tab={route.tab} friend={route.friend} />}
          {route.name === 'stats' && <Stats />}
          {route.name === 'wrapped' && <Wrapped key={`${route.year}:${route.scope}`} year={route.year} scope={route.scope === 'all' ? 'all' : 'me'} />}
          {route.name === 'profilesAdmin' && <Settings key="users" section="users" />}
          {route.name === 'together' && <TogetherJoin initial={route.code} />}
          {route.name === 'remote' && <RemotePage initial={route.code} />}
          {route.name === 'browse' && <Browse key={`${route.kind}:${route.id}`} kind={route.kind} id={route.id} media={route.media} />}
          {route.name === 'status' && <StatusPage />}
          {route.name === 'studio' && <SkinStudio key={route.id ?? 'new'} themeId={route.id} />}
          {route.name === 'addonTitle' && <AddonTitle key={`${route.addonId}:${route.id}`} addonId={route.addonId} type={route.type} id={route.id} />}
          {route.name === 'server' && <ServerPage tab={route.tab} />}
          {route.name === 'sportcenter' && <SportCenterPage tab={route.tab} params={route.params} />}
          {route.name === 'music' && <Music section={route.section} params={route.params} />}
          {route.name === 'person' && <PersonPage key={`${route.id}:${route.lookup}`} id={route.id} lookup={route.lookup} />}
        </>
      )}
      </ErrorBoundary>
      {route.name !== 'play' && route.name !== 'livePlay' && route.name !== 'addonPlay' && route.name !== 'multiview' && <MiniPlayer />}
      {route.name !== 'play' && route.name !== 'livePlay' && route.name !== 'addonPlay' && route.name !== 'multiview' && <FullPlayer />}
      <ReminderToasts />
      {route.name !== 'play' && route.name !== 'livePlay' && route.name !== 'addonPlay' && route.name !== 'multiview' && <TrophyWatcher />}
      <SportWatcher enabled={!!me.profile} />
      <RequestWatcher />
      <PlexSlowNotice />
      <RowArrows />
      <Screensaver disabled={route.name === 'play' || route.name === 'livePlay' || route.name === 'addonPlay' || route.name === 'multiview' || route.name === 'studio' || musicPlaying()} />
      <BedtimeGate />
      <RemoteScreen />
      <CardMenuHost />
      {(route.name === 'home' || route.name === 'hub') && <SeasonalLayer />}
    </Ctx.Provider>
  );
}
